/**
 * MFA challenge step shown inside the login card after a correct password.
 *
 * Handles both branches the backend returns from /auth/login:
 * - mfa_required          → verify an enrolled method (TOTP or email OTP).
 * - mfa_enrollment_required → force enrollment, then complete login.
 */
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Loader2, Mail, ShieldCheck, Smartphone } from 'lucide-react';

import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { Checkbox } from '../../../components/ui/checkbox';
import { RecoveryCodesPanel } from '../../../shared/components/mfa/RecoveryCodesPanel';
import { TotpEnrollPanel } from '../../../shared/components/mfa/TotpEnrollPanel';
import { authApi, AuthApiError } from '../../../shared/auth';
import type { LoginResponse, MfaChallengeResponse, MfaMethod } from '../../../shared/auth';
import { getBrowserTimezone } from '../../../shared/utils/timezone';
import { getGenericErrorMessage } from '../../../shared/utils/errors';
import { useApplyChallengeLanguage } from '../../../i18n';

type Step = 'choose' | 'totp-setup' | 'verify' | 'recovery';

interface MfaChallengeStepProps {
  challenge: MfaChallengeResponse;
  /** Email being signed in — shown on the email-OTP screen. */
  email: string;
  /** Called with the completed-login response once the challenge passes. */
  onAuthenticated: (response: LoginResponse) => void;
  /** Return to the password form. */
  onBack: () => void;
}

export function MfaChallengeStep({
  challenge,
  email,
  onAuthenticated,
  onBack,
}: MfaChallengeStepProps) {
  const { t } = useTranslation('auth');

  // The 2FA card renders before a JWT is issued, so switch into the account
  // language the backend put on the challenge (unless the user explicitly picked
  // one on the login screen). See useApplyChallengeLanguage.
  useApplyChallengeLanguage(challenge.preferred_language);

  const [step, setStep] = useState<Step>(challenge.mfa_required ? 'verify' : 'choose');
  const [challengeToken, setChallengeToken] = useState(challenge.challenge_token);
  const [method, setMethod] = useState<MfaMethod>(challenge.mfa_method ?? 'totp');
  const [totpData, setTotpData] = useState<{ secret: string; provisioning_uri: string } | null>(
    null,
  );
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [pendingLogin, setPendingLogin] = useState<LoginResponse | null>(null);

  const [code, setCode] = useState('');
  const [rememberDevice, setRememberDevice] = useState(false);
  // TOTP login defaults to a numeric code; the user can opt into entering an
  // alphanumeric recovery code instead (recovery codes look like "abcde-fghjk").
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const codeInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (step === 'verify') {
      codeInputRef.current?.focus();
    }
  }, [step]);

  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resent, setResent] = useState(false);

  const allowedMethods = challenge.allowed_methods ?? ['totp', 'email'];

  const handleError = (err: unknown) => {
    setError(err instanceof AuthApiError ? err.message : getGenericErrorMessage());
  };

  const handleChooseMethod = async (chosen: MfaMethod) => {
    setError(null);
    setIsLoading(true);
    try {
      const result = await authApi.mfaEnrollLoginStart({
        challenge_token: challengeToken,
        method: chosen,
      });
      if (chosen === 'totp' && result.secret && result.provisioning_uri) {
        setTotpData({ secret: result.secret, provisioning_uri: result.provisioning_uri });
        setStep('totp-setup');
      } else if (chosen === 'email' && result.challenge_token) {
        setChallengeToken(result.challenge_token);
        setMethod('email');
        setCode('');
        setStep('verify');
      }
    } catch (err) {
      handleError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTotpSetupConfirm = async (totpCode: string) => {
    setError(null);
    setIsLoading(true);
    try {
      const response = await authApi.mfaEnrollLoginConfirm({
        challenge_token: challengeToken,
        code: totpCode,
        remember_device: rememberDevice,
        timezone: getBrowserTimezone(),
      });
      setPendingLogin(response);
      setRecoveryCodes(response.recovery_codes ?? []);
      setStep('recovery');
    } catch (err) {
      handleError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      const response = await authApi.mfaVerify({
        challenge_token: challengeToken,
        code: code.trim(),
        remember_device: rememberDevice,
        timezone: getBrowserTimezone(),
      });
      onAuthenticated(response);
    } catch (err) {
      handleError(err);
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setError(null);
    setIsResending(true);
    try {
      await authApi.mfaResendOtp(challengeToken);
      setResent(true);
      setTimeout(() => setResent(false), 4000);
    } catch (err) {
      handleError(err);
    } finally {
      setIsResending(false);
    }
  };

  // Dev-only: the backend sets localhost_exemption_available only when running
  // locally (non-production + localhost). Skips the second factor and completes
  // login. The server re-checks the gate, so a forged flag cannot bypass MFA.
  const localhostExempt = challenge.localhost_exemption_available === true;

  const handleLocalhostExempt = async () => {
    setError(null);
    setIsLoading(true);
    try {
      const response = await authApi.mfaLocalhostExempt({
        challenge_token: challengeToken,
        timezone: getBrowserTimezone(),
      });
      onAuthenticated(response);
    } catch (err) {
      handleError(err);
      setIsLoading(false);
    }
  };

  // --- Recovery codes shown after a forced TOTP enrollment ---
  if (step === 'recovery') {
    return (
      <div className="space-y-4">
        <RecoveryCodesPanel
          codes={recoveryCodes}
          doneLabel={t('mfa.recoveryCodes.continueToApp')}
          onDone={() => pendingLogin && onAuthenticated(pendingLogin)}
        />
      </div>
    );
  }

  // --- Forced TOTP enrollment: QR + confirm ---
  if (step === 'totp-setup' && totpData) {
    return (
      <div className="space-y-4">
        <TotpEnrollPanel
          provisioningUri={totpData.provisioning_uri}
          secret={totpData.secret}
          onConfirm={handleTotpSetupConfirm}
          isConfirming={isLoading}
          error={error}
        />
        <RememberDeviceCheckbox checked={rememberDevice} onChange={setRememberDevice} />
        <BackButton label={t('mfa.chooseAnotherMethod')} onClick={() => setStep('choose')} />
      </div>
    );
  }

  // --- Forced enrollment: choose a method ---
  if (step === 'choose') {
    return (
      <div className="space-y-4">
        <Header
          icon={<ShieldCheck className="h-5 w-5 text-primary" />}
          title={t('mfa.enroll.title')}
          description={t('mfa.enroll.description')}
        />
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <div className="space-y-2">
          {allowedMethods.includes('totp') && (
            <MethodButton
              icon={<Smartphone className="h-5 w-5" />}
              title={t('mfa.enroll.totpTitle')}
              description={t('mfa.enroll.totpDescription')}
              disabled={isLoading}
              onClick={() => handleChooseMethod('totp')}
            />
          )}
          {allowedMethods.includes('email') && (
            <MethodButton
              icon={<Mail className="h-5 w-5" />}
              title={t('mfa.enroll.emailTitle')}
              description={t('mfa.enroll.emailDescription')}
              disabled={isLoading}
              onClick={() => handleChooseMethod('email')}
            />
          )}
        </div>
        {localhostExempt && (
          <LocalhostExemptNotice onSkip={handleLocalhostExempt} disabled={isLoading} />
        )}
        <BackButton label={t('mfa.backToLogin')} onClick={onBack} />
      </div>
    );
  }

  // --- Verify an enrolled method ---
  const isEmail = method === 'email';
  // Recovery codes only exist for TOTP enrollment (email MFA has none), so the
  // toggle is offered only on the authenticator branch. Email and the TOTP
  // numeric default both strip to 6 digits; recovery mode accepts raw text.
  const numericEntry = !useRecoveryCode;
  const toggleRecoveryCode = (next: boolean) => {
    setUseRecoveryCode(next);
    setCode('');
    setError(null);
    codeInputRef.current?.focus();
  };
  return (
    <form onSubmit={handleVerify} className="space-y-4">
      <Header
        icon={
          isEmail ? (
            <Mail className="h-5 w-5 text-primary" />
          ) : (
            <ShieldCheck className="h-5 w-5 text-primary" />
          )
        }
        title={t('mfa.verify.title')}
        description={
          isEmail
            ? t('mfa.verify.emailDescription', { email })
            : t('mfa.verify.totpDescription')
        }
      />

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {resent && (
        <Alert>
          <AlertDescription>{t('mfa.verify.resent')}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-2">
        <Label htmlFor="mfa-verify-code">
          {useRecoveryCode ? t('mfa.verify.recoveryCodeLabel') : t('mfa.verify.codeLabel')}
        </Label>
        <Input
          ref={codeInputRef}
          id="mfa-verify-code"
          value={code}
          onChange={(e) =>
            setCode(numericEntry ? e.target.value.replace(/\D/g, '').slice(0, 6) : e.target.value)
          }
          inputMode={numericEntry ? 'numeric' : 'text'}
          autoComplete="one-time-code"
          placeholder={numericEntry ? '000000' : t('mfa.verify.recoveryPlaceholder')}
          className={`text-center font-mono text-lg ${numericEntry ? 'tracking-[0.3em]' : ''}`}
          data-testid="mfa-verify-code-input"
        />
        {!isEmail &&
          (useRecoveryCode ? (
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs text-muted-foreground">{t('mfa.verify.recoveryHint')}</p>
              <button
                type="button"
                onClick={() => toggleRecoveryCode(false)}
                className="flex-shrink-0 text-xs text-primary hover:underline"
                data-testid="mfa-use-authenticator-code"
              >
                {t('mfa.verify.useAuthenticatorCode')}
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => toggleRecoveryCode(true)}
              className="text-xs text-primary hover:underline"
              data-testid="mfa-use-recovery-code"
            >
              {t('mfa.verify.useRecoveryCode')}
            </button>
          ))}
      </div>

      <RememberDeviceCheckbox checked={rememberDevice} onChange={setRememberDevice} />

      <Button
        type="submit"
        className="w-full"
        size="lg"
        disabled={isLoading || code.trim().length < (numericEntry ? 6 : 1)}
        data-testid="mfa-verify-button"
      >
        {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : t('mfa.verify.submit')}
      </Button>

      <div className="flex items-center justify-between">
        <BackButton label={t('mfa.backToLogin')} onClick={onBack} inline />
        {isEmail && (
          <button
            type="button"
            onClick={handleResend}
            disabled={isResending}
            className="text-sm text-primary hover:underline disabled:opacity-50"
          >
            {isResending ? t('mfa.verify.resending') : t('mfa.verify.resend')}
          </button>
        )}
      </div>

      {localhostExempt && (
        <LocalhostExemptNotice onSkip={handleLocalhostExempt} disabled={isLoading} />
      )}
    </form>
  );
}

/**
 * Dev-only escape hatch shown on the MFA card when the backend reports it is
 * running locally (non-production + localhost). Lets a developer skip the
 * second factor. Rendered only when `localhost_exemption_available` is set, and
 * the server re-checks the gate — it can never appear or work in production.
 */
function LocalhostExemptNotice({
  onSkip,
  disabled,
}: {
  onSkip: () => void;
  disabled: boolean;
}) {
  const { t } = useTranslation('auth');
  return (
    <div className="rounded-lg border border-dashed border-border bg-muted/30 p-3 text-center">
      <p className="text-xs text-muted-foreground">{t('mfa.localhostExempt.note')}</p>
      <button
        type="button"
        onClick={onSkip}
        disabled={disabled}
        className="mt-1 text-sm font-medium text-primary hover:underline disabled:opacity-50"
        data-testid="mfa-localhost-exempt"
      >
        {t('mfa.localhostExempt.action')}
      </button>
    </div>
  );
}

function Header({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary/10">
        {icon}
      </div>
      <div className="space-y-1">
        <h3 className="text-base font-semibold">{title}</h3>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
    </div>
  );
}

function MethodButton({
  icon,
  title,
  description,
  disabled,
  onClick,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex w-full items-center gap-3 rounded-lg border border-border bg-card p-3 text-left transition-colors hover:border-primary/50 hover:bg-accent disabled:opacity-50"
    >
      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-sm font-medium">{title}</p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
    </button>
  );
}

function RememberDeviceCheckbox({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  const { t } = useTranslation('auth');
  return (
    <div className="flex items-start gap-2">
      <Checkbox
        id="mfa-remember-device"
        checked={checked}
        onCheckedChange={(v) => onChange(v === true)}
        className="mt-0.5"
      />
      <Label
        htmlFor="mfa-remember-device"
        className="text-sm font-normal leading-snug text-muted-foreground"
      >
        {t('mfa.rememberDevice')}
      </Label>
    </div>
  );
}

function BackButton({
  label,
  onClick,
  inline,
}: {
  label: string;
  onClick: () => void;
  inline?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground ${
        inline ? '' : 'w-full justify-center pt-1'
      }`}
    >
      <ArrowLeft className="h-4 w-4" />
      {label}
    </button>
  );
}
